import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Serviço para gerenciar assinaturas
 */

const COLECAO_ASSINATURAS = 'assinaturas';
const VALOR_FRETE_NORMAL = 5.00;
const VALOR_FRETE_ASSINANTE = 2.50;
const PONTOS_POR_REAL_NORMAL = 1;
const PONTOS_POR_REAL_ASSINANTE = 2;

/**
 * Verifica se o usuário tem assinatura ativa
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, temAssinatura: boolean, dados?: object}>}
 */
export const verificarAssinatura = async (userId) => {
  try {
    if (!userId) {
      return { success: false, temAssinatura: false, error: 'Usuário não autenticado' };
    }

    const assinaturaRef = doc(db, COLECAO_ASSINATURAS, userId);
    const assinaturaSnap = await getDoc(assinaturaRef);

    if (!assinaturaSnap.exists()) {
      return { success: true, temAssinatura: false };
    }

    const dados = assinaturaSnap.data();
    const agora = new Date();
    const dataExpiracao = dados.dataExpiracao?.toDate();

    // ✅ CORREÇÃO: Verificar dataExpiracao mesmo se cancelada
    // Se ainda não expirou (mesmo que cancelada), manter benefícios
    // Cliente pagou pelo período, tem direito aos benefícios até expirar
    if (dataExpiracao && dataExpiracao > agora) {
      return {
        success: true,
        temAssinatura: true, // Benefícios ativos até dataExpiracao
        dados: {
          ...dados,
          dataExpiracao: dataExpiracao,
          cancelada: !dados.ativa // Flag para saber se foi cancelada (mas ainda ativa até expirar)
        }
      };
    }

    // Se expirou, marca como inativa (se ainda não estiver marcada)
    if (dados.ativa && dataExpiracao && dataExpiracao <= agora) {
      await updateDoc(assinaturaRef, {
        ativa: false,
        motivoInativacao: 'Expirada',
        atualizadoEm: serverTimestamp()
      });
      return { success: true, temAssinatura: false };
    }

    // Se já estava cancelada e expirou, apenas retorna sem assinatura
    return { success: true, temAssinatura: false };
  } catch (error) {
    console.error('❌ Erro ao verificar assinatura:', error);
    return { success: false, temAssinatura: false, error: error.message };
  }
};

/**
 * Cria uma nova assinatura
 * @param {string} userId - ID do usuário
 * @param {number} meses - Quantidade de meses (padrão: 1)
 * @returns {Promise<{success: boolean, assinatura?: object, error?: string}>}
 */
export const criarAssinatura = async (userId, meses = 1, customerId = null, subscriptionId = null) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const agora = new Date();
    const dataExpiracao = new Date(agora);
    dataExpiracao.setMonth(dataExpiracao.getMonth() + meses);

    const assinaturaRef = doc(db, COLECAO_ASSINATURAS, userId);
    const assinaturaData = {
      userId,
      ativa: true,
      dataInicio: serverTimestamp(),
      dataExpiracao: dataExpiracao,
      meses: meses,
      beneficios: {
        freteDesconto: true,
        freteValor: VALOR_FRETE_ASSINANTE,
        prioridadeEntrega: true,
        pontosDuplos: true
      },
      criadoEm: serverTimestamp(),
      atualizadoEm: serverTimestamp()
    };

    // Adicionar dados do Stripe se disponíveis (para cobrança recorrente)
    if (customerId) {
      assinaturaData.customerId = customerId;
    }
    if (subscriptionId) {
      assinaturaData.subscriptionId = subscriptionId;
      assinaturaData.stripeStatus = 'active';
    }

    await setDoc(assinaturaRef, assinaturaData);

    console.log(`✅ Assinatura criada para usuário ${userId} - Expira em ${dataExpiracao.toLocaleDateString()}`);

    return {
      success: true,
      assinatura: {
        ...assinaturaData,
        dataInicio: agora,
        dataExpiracao: dataExpiracao
      }
    };
  } catch (error) {
    console.error('❌ Erro ao criar assinatura:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Renova uma assinatura existente
 * @param {string} userId - ID do usuário
 * @param {number} meses - Quantidade de meses para adicionar
 * @returns {Promise<{success: boolean, assinatura?: object, error?: string}>}
 */
export const renovarAssinatura = async (userId, meses = 1) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const assinaturaRef = doc(db, COLECAO_ASSINATURAS, userId);
    const assinaturaSnap = await getDoc(assinaturaRef);

    if (!assinaturaSnap.exists()) {
      return criarAssinatura(userId, meses);
    }

    const dadosAtuais = assinaturaSnap.data();
    let novaDataExpiracao;

    // Se ainda tem assinatura ativa, adiciona meses à data atual de expiração
    if (dadosAtuais.ativa && dadosAtuais.dataExpiracao) {
      const dataAtualExpiracao = dadosAtuais.dataExpiracao.toDate();
      novaDataExpiracao = new Date(dataAtualExpiracao);
      novaDataExpiracao.setMonth(novaDataExpiracao.getMonth() + meses);
    } else {
      // Se não tem assinatura ativa, cria nova a partir de hoje
      const agora = new Date();
      novaDataExpiracao = new Date(agora);
      novaDataExpiracao.setMonth(novaDataExpiracao.getMonth() + meses);
    }

    await updateDoc(assinaturaRef, {
      ativa: true,
      dataExpiracao: novaDataExpiracao,
      meses: (dadosAtuais.meses || 0) + meses,
      atualizadoEm: serverTimestamp()
    });

    console.log(`✅ Assinatura renovada para usuário ${userId} - Nova expiração: ${novaDataExpiracao.toLocaleDateString()}`);

    return {
      success: true,
      assinatura: {
        ...dadosAtuais,
        ativa: true,
        dataExpiracao: novaDataExpiracao,
        meses: (dadosAtuais.meses || 0) + meses
      }
    };
  } catch (error) {
    console.error('❌ Erro ao renovar assinatura:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancela uma assinatura (não remove, apenas marca como inativa)
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const cancelarAssinatura = async (userId, cancelarNoStripe = false) => {
  try {
    if (!userId) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    const assinaturaRef = doc(db, COLECAO_ASSINATURAS, userId);
    const assinaturaSnap = await getDoc(assinaturaRef);

    if (!assinaturaSnap.exists()) {
      return { success: false, error: 'Assinatura não encontrada' };
    }

    const assinaturaData = assinaturaSnap.data();

    // Se tiver subscriptionId e cancelarNoStripe for true, cancelar no Stripe também
    if (cancelarNoStripe && assinaturaData.subscriptionId) {
      // Importar dinamicamente para evitar dependência circular
      const { cancelStripeSubscription } = await import('./stripeSubscriptionService');
      const stripeResult = await cancelStripeSubscription(assinaturaData.subscriptionId);
      
      if (!stripeResult.success) {
        console.warn('⚠️ Erro ao cancelar no Stripe, mas continuando cancelamento local:', stripeResult.error);
      } else {
        console.log('✅ Subscription cancelada no Stripe');
      }
    }

    await updateDoc(assinaturaRef, {
      ativa: false,
      motivoInativacao: 'Cancelada pelo usuário',
      canceladaEm: serverTimestamp(),
      atualizadoEm: serverTimestamp()
    });

    console.log(`✅ Assinatura cancelada para usuário ${userId}`);

    return { success: true };
  } catch (error) {
    console.error('❌ Erro ao cancelar assinatura:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calcula o valor do frete baseado na assinatura
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, valorFrete: number, temDesconto: boolean}>}
 */
export const calcularFrete = async (userId) => {
  try {
    const assinatura = await verificarAssinatura(userId);
    
    if (assinatura.success && assinatura.temAssinatura) {
      return {
        success: true,
        valorFrete: VALOR_FRETE_ASSINANTE,
        temDesconto: true,
        valorNormal: VALOR_FRETE_NORMAL,
        desconto: VALOR_FRETE_NORMAL - VALOR_FRETE_ASSINANTE
      };
    }

    return {
      success: true,
      valorFrete: VALOR_FRETE_NORMAL,
      temDesconto: false,
      valorNormal: VALOR_FRETE_NORMAL,
      desconto: 0
    };
  } catch (error) {
    console.error('❌ Erro ao calcular frete:', error);
    return {
      success: false,
      valorFrete: VALOR_FRETE_NORMAL,
      temDesconto: false,
      error: error.message
    };
  }
};

/**
 * Retorna a quantidade de pontos por real baseado na assinatura
 * @param {string} userId - ID do usuário
 * @returns {Promise<{success: boolean, pontosPorReal: number, temBonus: boolean}>}
 */
export const obterPontosPorReal = async (userId) => {
  try {
    const assinatura = await verificarAssinatura(userId);
    
    if (assinatura.success && assinatura.temAssinatura) {
      return {
        success: true,
        pontosPorReal: PONTOS_POR_REAL_ASSINANTE,
        temBonus: true
      };
    }

    return {
      success: true,
      pontosPorReal: PONTOS_POR_REAL_NORMAL,
      temBonus: false
    };
  } catch (error) {
    console.error('❌ Erro ao obter pontos por real:', error);
    return {
      success: false,
      pontosPorReal: PONTOS_POR_REAL_NORMAL,
      temBonus: false,
      error: error.message
    };
  }
};

// Exportar constantes para uso em outros lugares
export const VALORES_FRETE = {
  NORMAL: VALOR_FRETE_NORMAL,
  ASSINANTE: VALOR_FRETE_ASSINANTE
};

export const PONTOS_CONFIG = {
  NORMAL: PONTOS_POR_REAL_NORMAL,
  ASSINANTE: PONTOS_POR_REAL_ASSINANTE
};

